<script setup lang="ts">
  import { ref, onMounted, computed, inject, type Ref } from 'vue'
  import { useToast } from '@/components/ui/toast'
  import { useFieldValidation } from '@/composables/useQuestionnaire'
  import { useTranslation } from '@/composables/useTranslation'
  import { type ClientQuestionnaireSubmission } from '@/types/generated'
  import { QuestionnaireSubmissionsService } from '@/types/generated'
  import MultiFileField from '@/components/admin/form/MultiFileField.vue'
  import type { Page } from '@/types'

  const questionnaire = inject<Ref<ClientQuestionnaireSubmission | null>>(
    'questionnaire',
    ref(null)
  )
  const rawSchema = inject<Ref<Page[]>>('rawSchema', ref([]))
  const { toast, dismiss: dismissToast } = useToast()
  const { t } = useTranslation({
    en: {
      success: 'Success',
      error: 'Error',
      fileDeletedSuccessfully: 'File deleted successfully',
      errorDeletingFile: 'Error deleting file',
      questionnaireNotFound: 'Questionnaire not found',
      fieldPositionError: 'Field position could not be determined',
      fileAlreadyExists: 'File "{name}" already exists',
      fileUploadFailed: 'File "{name}" could not be uploaded',
      filesUploadFailed: '{count} file could not be uploaded',
      filesUploadFailedPlural: '{count} files could not be uploaded',
      warning: 'Warning',
      fileUploadedSuccess: 'File uploaded successfully',
      filesUploadedSuccess: '{count} file uploaded successfully',
      filesUploadedSuccessPlural: '{count} files uploaded successfully',
      uploadError: 'Error uploading files',
      uploadingFiles: 'Uploading files...'
    },
    de: {
      success: 'Erfolg',
      error: 'Fehler',
      fileDeletedSuccessfully: 'Datei erfolgreich gelöscht',
      errorDeletingFile: 'Fehler beim Löschen der Datei',
      questionnaireNotFound: 'Questionnaire nicht gefunden',
      fieldPositionError: 'Feldposition konnte nicht ermittelt werden',
      fileAlreadyExists: 'Datei "{name}" ist bereits vorhanden',
      fileUploadFailed: 'Datei "{name}" konnte nicht hochgeladen werden',
      filesUploadFailed: '{count} Datei konnte nicht hochgeladen werden',
      filesUploadFailedPlural: '{count} Dateien konnten nicht hochgeladen werden',
      warning: 'Warnung',
      fileUploadedSuccess: 'Datei erfolgreich hochgeladen',
      filesUploadedSuccess: '{count} Datei erfolgreich hochgeladen',
      filesUploadedSuccessPlural: '{count} Dateien erfolgreich hochgeladen',
      uploadError: 'Fehler beim Hochladen der Dateien',
      uploadingFiles: 'Dateien werden hochgeladen...'
    }
  })

  const emit = defineEmits(['change', 'update:modelValue', 'filesChanged', 'validation-error'])

  const isUploading = ref(false)
  const files = ref<any[]>([])
  const multiFileRef = ref()

  const props = defineProps({
    field: {
      type: Object,
      default: () => ({})
    },
    modelValue: {
      type: Array as () => any[],
      default: () => []
    },
    isPreviewMode: {
      type: Boolean,
      default: false
    },
    descriptionId: {
      type: String,
      required: false
    }
  })

  const maxFiles = computed(() => {
    // We don't want to limit the number of files for single file fields yet.
    // return props.field.type === 'filefield' ? 1 : undefined
    return undefined
  })

  const { validationError, handleInputChange } = useFieldValidation(props, emit)

  const errorId = computed(() => `${props.field.uuid}-error`)

  const findFieldPosition = (fieldUuid: string) => {
    if (!rawSchema.value || !Array.isArray(rawSchema.value)) {
      return null
    }

    for (let [pageIndex, page] of rawSchema.value.entries()) {
      if (page.items && Array.isArray(page.items)) {
        for (let [itemIndex, item] of page.items.entries()) {
          if (item.uuid === fieldUuid) {
            return { page: pageIndex, index: itemIndex }
          }
        }
      }
    }
    return null
  }

  const handleFilesChanged = async (newFiles: File[]) => {
    dismissToast()

    if (!questionnaire.value?.uuid) {
      toast({
        title: t('error'),
        description: t('questionnaireNotFound'),
        variant: 'destructive'
      })
      return
    }

    const fieldPosition = findFieldPosition(props.field.uuid)
    if (!fieldPosition) {
      toast({
        title: t('error'),
        description: t('fieldPositionError'),
        variant: 'destructive'
      })
      return
    }

    isUploading.value = true

    try {
      const uploadedFiles = []
      const failureMessages = []

      console.log('newFiles', newFiles)

      for (const file of newFiles) {
        try {
          const response =
            await QuestionnaireSubmissionsService.questionnaireSubmissionsUploadFileCreate({
              uuid: questionnaire.value.uuid,
              formData: {
                file: file,
                page_index: fieldPosition.page,
                item_index: fieldPosition.index
              }
            })

          console.log('response', response)
          if (response) {
            uploadedFiles.push({
              ...response,
              name: file.name,
              size: file.size,
              type: file.type
            })
          }
          console.log('uploadedFiles', uploadedFiles)
        } catch (error) {
          if (
            typeof error === 'object' &&
            error !== null &&
            'status' in error &&
            error.status === 409
          ) {
            failureMessages.push(t('fileAlreadyExists', { name: file.name }))
          } else {
            failureMessages.push(t('fileUploadFailed', { name: file.name }))
          }
        }
      }
      const updatedFiles = [...files.value, ...uploadedFiles]
      files.value = updatedFiles
      handleInputChange(updatedFiles)
      emit('filesChanged', updatedFiles)
      multiFileRef.value?.resetStage() // Avoid re-upload on next attempt

      // We prioritize bad news, because only one toast is shown at a time
      if (failureMessages.length > 0) {
        const summaryMessage =
          failureMessages.length === 1
            ? t('filesUploadFailed', { count: failureMessages.length })
            : t('filesUploadFailedPlural', { count: failureMessages.length })
        failureMessages.push(summaryMessage)
        const description = failureMessages.join('\n')
        toast({
          title: t('warning'),
          description: description,
          variant: 'destructive'
        })
      } else {
        const isSingleFile = uploadedFiles.length === 1
        const message = isSingleFile
          ? t('fileUploadedSuccess')
          : uploadedFiles.length > 1
            ? t('filesUploadedSuccessPlural', { count: uploadedFiles.length })
            : t('filesUploadedSuccess', { count: uploadedFiles.length })

        toast({
          title: t('success'),
          description: message,
          variant: 'default'
        })
      }
    } catch (error) {
      console.error('Upload error:', error)
      toast({
        title: t('error'),
        description: t('uploadError'),
        variant: 'destructive'
      })
    } finally {
      isUploading.value = false
    }
  }

  const handleFileRemoved = async (file: any, index: number) => {
    const fileUuid = file.uuid
    if (fileUuid && questionnaire.value?.short_id) {
      try {
        const urlParams = new URLSearchParams(window.location.search)
        const accessToken = urlParams.get('access_token')
        const sessionToken = urlParams.get('session_token')

        const response = await fetch(
          `/api/v3/questionnaire_submissions/${questionnaire.value?.short_id || ''}/delete-file/`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken':
                document.cookie
                  .split('; ')
                  .find((row) => row.startsWith('csrftoken='))
                  ?.split('=')[1] || '',
              ...(accessToken && { 'X-Access-Token': accessToken }),
              ...(sessionToken && { 'X-Session-Token': sessionToken })
            },
            body: JSON.stringify({
              uuid: fileUuid
            })
          }
        )

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        toast({
          title: t('success'),
          description: t('fileDeletedSuccessfully'),
          variant: 'default'
        })
      } catch (error) {
        console.error('Error deleting file:', error)
        toast({
          title: t('error'),
          description: t('errorDeletingFile'),
          variant: 'destructive'
        })
        return
      }
    }

    const updatedFiles = files.value.filter((f: any) => f.uuid !== fileUuid || f.name !== file.name)
    files.value = updatedFiles
    handleInputChange(updatedFiles)
  }

  onMounted(() => {
    const fieldPosition = findFieldPosition(props.field.uuid)
    if (fieldPosition && rawSchema.value) {
      try {
        const schemaField = rawSchema.value[fieldPosition.page]?.items[fieldPosition.index]
        if (schemaField && schemaField.value && Array.isArray(schemaField.value)) {
          const schemaFiles = schemaField.value.map((file: any) => ({
            uuid: file.uuid,
            url: file.url,
            filename: file.filename,
            title: file.title,
            name: file.filename || file.title,
            size: file.size || 0,
            type: file.type || 'application/octet-stream',
            hash: file.hash
          }))
          files.value = schemaFiles
          handleInputChange(schemaFiles)
        }
      } catch (error) {
        console.error('Error updating files from schema:', error)
      }
    }
  })
</script>

<template>
  <div class="w-full pb-4">
    <MultiFileField
      ref="multiFileRef"
      :key="files.length"
      :name="field.uuid"
      :allowed-file-extensions="['pdf', 'png', 'jpg', 'jpeg']"
      :disabled="isPreviewMode"
      :model-value="files"
      :max-files="maxFiles"
      @update:model-value="handleFilesChanged"
      @file-removed="handleFileRemoved"
    />

    <div
      v-if="validationError"
      :id="errorId"
      class="mt-2 text-sm text-red-600"
      aria-live="polite"
    >
      {{ validationError }}
    </div>

    <div
      v-if="isUploading"
      class="mt-4 flex items-center justify-center"
      role="status"
      aria-live="polite"
    >
      <div
        class="h-6 w-6 animate-spin rounded-full border-b-2 border-gray-900"
        aria-hidden="true"
      ></div>
      <span class="ml-2 text-sm text-gray-600">{{ t('uploadingFiles') }}</span>
    </div>
  </div>
</template>

<style scoped></style>
